import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, ArrowLeft, Pencil } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../../../../services/api';

const STATUS_COLORS = {
    New: 'bg-blue-100 text-blue-800', Contacted: 'bg-indigo-100 text-indigo-800',
    Qualified: 'bg-purple-100 text-purple-800', Pending: 'bg-yellow-100 text-yellow-800',
    Converted: 'bg-green-100 text-green-800', Lost: 'bg-red-100 text-red-800',
};

export default function ReferralSourceDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [source, setSource] = useState(null);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get(`/referral-sources/${id}`),
            api.get(`/referral-sources/${id}/leads`),
        ])
            .then(([sRes, lRes]) => { setSource(sRes.data); setLeads(lRes.data); })
            .catch(() => toast.error('Failed to load'))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>;
    if (!source) return <div className="text-center py-20 text-gray-400">Not found</div>;

    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
            <Toaster position="top-right" richColors />
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate('/admin/referral-sources')} className="p-2 hover:bg-gray-200 rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
                        <h2 className="text-2xl font-bold text-gray-900">{source.facility_name}</h2>
                    </div>
                    <button onClick={() => navigate(`/admin/referral-sources/${id}/edit`)} className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                        <Pencil className="w-4 h-4" /> Edit
                    </button>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[['Contact Person', source.contact_person || '—'],['Phone', source.phone || '—'],['Email', source.email || '—'],['Address', source.address || '—'],['Notes', source.notes || '—']].map(([label, value]) => (
                        <div key={label}>
                            <p className="text-xs font-semibold text-gray-400 uppercase">{label}</p>
                            <p className="text-gray-800 mt-0.5">{value}</p>
                        </div>
                    ))}
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <h2 className="font-semibold text-gray-700 mb-4">Linked Leads ({leads.length})</h2>
                    {leads.length === 0 ? (
                        <p className="text-gray-400 text-sm">No leads linked to this referral source.</p>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-100">
                            <thead><tr>{['Name','Phone','Service','Status','Assigned'].map(h => <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr></thead>
                            <tbody className="divide-y divide-gray-50">
                                {leads.map(l => (
                                    <tr key={l.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/admin/leads/${l.id}`)}>
                                        <td className="px-4 py-2 font-medium text-gray-800">{l.name}</td>
                                        <td className="px-4 py-2 text-sm text-gray-600">{l.phone}</td>
                                        <td className="px-4 py-2 text-sm text-gray-600">{l.service_requested}</td>
                                        <td className="px-4 py-2"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[l.status] || 'bg-gray-100 text-gray-700'}`}>{l.status}</span></td>
                                        <td className="px-4 py-2 text-sm text-gray-600">{l.assigned_staff?.name || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
